import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export type TaskManagerType =
  | "todoist"
  | "linear"
  | "asana"
  | "trello"
  | "google_tasks"
  | null;

export interface SessionCompletionData {
  id: string;
  startedAt: string;
  endedAt: string | null;
  targetGoal: number;
  finalPoints: number;
  duration: number;
  tasksWorkedOn: number;
  tasksCompleted: number;
  bonusApplied?: { amount: number; bonusId?: string } | null;
  bonusAwarded?: { amount: number; usableOnDate?: string } | null;
}

const MAX_SESSION_POINTS = 1000;

function normalizeSessionCompletionData(
  data: SessionCompletionData
): SessionCompletionData {
  const clampPoints = (value: number | undefined): number => {
    const n = Number(value);
    if (!Number.isFinite(n)) {
      return 0;
    }
    return Math.max(0, Math.min(MAX_SESSION_POINTS, Math.round(n)));
  };

  return {
    ...data,
    finalPoints: clampPoints(data.finalPoints),
    bonusApplied: data.bonusApplied
      ? { ...data.bonusApplied, amount: clampPoints(data.bonusApplied.amount) }
      : data.bonusApplied,
    bonusAwarded: data.bonusAwarded
      ? { ...data.bonusAwarded, amount: clampPoints(data.bonusAwarded.amount) }
      : data.bonusAwarded,
  };
}

export type SettingsMenu =
  | "main"
  | "profile"
  | "smartAssistant"
  | "customizePomodoroTimer"
  | "enablePhoneWebsiteDistractions"
  | "websiteDistractions"
  | "desktopAppDistractions"
  | "connectCalendars"
  | "connectTaskManager"
  | "customizeTasks"
  | "emailPreferences"
  | "socialMedia"
  | "connectTaskManager-todoist"
  | "connectTaskManager-linear"
  | "connectTaskManager-asana"
  | "connectTaskManager-trello"
  | "connectTaskManager-google_tasks"
  | "keyboardShortcuts"
  | null;

interface SettingsStore {
  isSettingsOpen: boolean;
  activeSettingsMenu: SettingsMenu;
  menuHistory: SettingsMenu[];
  activeTaskManager: TaskManagerType;
  oauthProcessing: boolean;
  phoneDistractionDialogOpen: boolean;
  /** Bumped when the phone distraction dialog is opened; single-use sign-in tokens must never reuse. */
  phoneDistractionDialogQrKey: number;
  sessionCompletionData: SessionCompletionData | null;
  showCompletionDialog: boolean;
  /** Where the session completion celebration is shown (`pip` vs main dashboard). */
  sessionCompletionSurface: "main" | "pip" | null;
  activeTab: boolean;
  musicMuted: boolean;
  /** Temporary silence for meetings: timer bells, assistant TTS, and music output (Howler). Client-only. */
  meetingAudioSilence: boolean;
  activeChatChannelId: string | null;
  setActiveTab: (state: boolean) => void;
  setPhoneDistractionDialogOpen: (state: boolean) => void;
  setSessionCompletionData: (data: SessionCompletionData | null) => void;
  setShowCompletionDialog: (open: boolean) => void;
  setSessionCompletionSurface: (surface: "main" | "pip" | null) => void;
  setActiveTaskManager: (taskManager: TaskManagerType) => void;
  openSettings: (menu?: SettingsMenu) => void;
  closeSettings: () => void;
  setActiveSettingsMenu: (menu: SettingsMenu) => void;
  setOauthProcessing: (state: boolean) => void;
  addToMenuHistory: (menu: SettingsMenu) => void;
  navigateBack: () => boolean;
  clearMenuHistory: () => void;
  navigateTo: (menu: SettingsMenu) => void;
  setMusicMuted: (muted: boolean) => void;
  setMeetingAudioSilence: (silence: boolean) => void;
  setActiveChatChannelId: (channelId: string | null) => void;
}

export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set) => ({
      isSettingsOpen: false,
      activeSettingsMenu: "main",
      menuHistory: ["main"],
      activeTaskManager: null,
      oauthProcessing: false,
      phoneDistractionDialogOpen: false,
      phoneDistractionDialogQrKey: 0,
      sessionCompletionData: null,
      showCompletionDialog: false,
      sessionCompletionSurface: null,
      activeTab: true,
      musicMuted: false,
      meetingAudioSilence: false,
      activeChatChannelId: null,
      setActiveTab: (state: boolean) => set({ activeTab: state }),
      setMusicMuted: (muted: boolean) => set({ musicMuted: muted }),
      setMeetingAudioSilence: (silence: boolean) =>
        set({ meetingAudioSilence: silence }),
      setActiveChatChannelId: (channelId: string | null) =>
        set({ activeChatChannelId: channelId }),
      setActiveTaskManager: (taskManager: TaskManagerType) =>
        set({ activeTaskManager: taskManager }),
      openSettings: (menu: SettingsMenu = "main") =>
        set({
          isSettingsOpen: true,
          activeSettingsMenu: menu,
        }),

      closeSettings: () =>
        set({
          isSettingsOpen: false,
          activeSettingsMenu: "main",
          activeTaskManager: null,
          menuHistory: ["main"],
          // Don't clear history on close to maintain navigation state
        }),

      setActiveSettingsMenu: (menu: SettingsMenu) =>
        set({
          activeSettingsMenu: menu,
          isSettingsOpen: true,
        }),

      setOauthProcessing: (state: boolean) =>
        set({
          oauthProcessing: state,
        }),

      setPhoneDistractionDialogOpen: (open: boolean) =>
        set((s) => {
          if (open) {
            const isOpening = !s.phoneDistractionDialogOpen;
            return {
              phoneDistractionDialogOpen: true,
              phoneDistractionDialogQrKey: isOpening
                ? s.phoneDistractionDialogQrKey + 1
                : s.phoneDistractionDialogQrKey,
            };
          }
          return { phoneDistractionDialogOpen: false };
        }),

      setSessionCompletionData: (data: SessionCompletionData | null) =>
        set({
          sessionCompletionData: data ? normalizeSessionCompletionData(data) : null,
        }),

      setShowCompletionDialog: (open: boolean) =>
        set({ showCompletionDialog: open }),

      setSessionCompletionSurface: (surface: "main" | "pip" | null) =>
        set({ sessionCompletionSurface: surface }),

      addToMenuHistory: (menu: SettingsMenu) =>
        set((state) => {
          // Only add to history if it's different from the current menu and not null
          if (menu && state.activeSettingsMenu !== menu) {
            const next = [
              ...state.menuHistory,
              state.activeSettingsMenu,
            ].filter(Boolean) as SettingsMenu[];
            return {
              menuHistory: next.slice(-25),
            };
          }
          return state;
        }),

      navigateTo: (menu: SettingsMenu) => {
        set((state) => {
          // Don't update if we're already on this menu
          if (state.activeSettingsMenu === menu) return state;

          const newHistory =
            state.activeSettingsMenu !== null &&
            state.activeSettingsMenu !== menu
              ? [...state.menuHistory, state.activeSettingsMenu]
              : [...state.menuHistory];

          const trimmed = newHistory.filter(Boolean) as SettingsMenu[];
          return {
            activeSettingsMenu: menu,
            isSettingsOpen: true,
            menuHistory: trimmed.slice(-25),
          };
        });
      },

      navigateBack: () => {
        let previousMenu: SettingsMenu | null = null;

        set((state) => {
          if (state.menuHistory.length === 0) return state;

          // Get and remove the last menu from history
          previousMenu = state.menuHistory[state.menuHistory.length - 1];
          const newHistory = state.menuHistory.slice(0, -1);
          if (newHistory.length === 0) {
            return {
              activeSettingsMenu: "main",
              menuHistory: ["main"],
            };
          }

          return {
            activeSettingsMenu: previousMenu,
            menuHistory: newHistory,
          };
        });

        return previousMenu !== null;
      },

      clearMenuHistory: () =>
        set({
          menuHistory: [],
        }),
    }),
    {
      name: "sukha-settings",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        isSettingsOpen: state.isSettingsOpen,
        activeSettingsMenu: state.activeSettingsMenu,
        menuHistory: state.menuHistory,
        musicMuted: state.musicMuted,
        meetingAudioSilence: state.meetingAudioSilence,
        activeChatChannelId: state.activeChatChannelId,
      }),
    }
  )
);
